import { apiClient } from '@/lib/apiClient';
import type { QueryOptions } from '@/lib/reactQuery';
import type { DateString } from '@/types/api';
import type { StudentRequestBody } from '@/types/scheduling';
import { useQuery } from '@tanstack/react-query';

export interface GetTimeslotsPerDayResponseData {
  timeslots: { date: DateString }[];
}

interface GetTimeslotsPerDayParams {
  students: StudentRequestBody[];
  date: DateString;
  reschedulingAppointmentId?: string;
  /**
   * The browser's IANA timezone. `date` is built with `startOfDay` in this zone and then
   * serialised with `toISOString()`, which drops the offset — so the backend needs the zone
   * name to know which calendar day was meant. See getCalendarPeriod.
   */
  timeZone?: string;
}

export const getTimeslotsPerDay = async ({ ...params }: GetTimeslotsPerDayParams) => {
  const response = await apiClient.post<GetTimeslotsPerDayResponseData>('/v1/scheduling/get-timeslots-per-day', { ...params });
  return response.data;
};

interface UseGetTimeslotsPerDayOptions {
  params: GetTimeslotsPerDayParams;
  options?: QueryOptions<GetTimeslotsPerDayResponseData>;
}

export const GET_TIMESLOTS_PER_DAY_QUERY_KEY = 'getTimeslotsPerDay';

export const useGetTimeslotsPerDay = ({ params, options }: UseGetTimeslotsPerDayOptions) => {
  return useQuery({
    queryKey: [GET_TIMESLOTS_PER_DAY_QUERY_KEY, params],
    queryFn: () => getTimeslotsPerDay(params),
    ...options,
  });
};
